import axios from "axios";
import { io } from "socket.io-client";

// API base URL
const API_URL =
  import.meta.env.VITE_CRM_API_URL || "https://dashboard.lono.co.in/api/notifications";

// Socket.io URL
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || "https://dashboard.lono.co.in";

// Axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor to attach token to all requests
api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("adminToken") ||
      localStorage.getItem("staffToken") ||
      localStorage.getItem("customerToken") ||
      localStorage.getItem("bankingPartnerToken") ||
      localStorage.getItem("affiliatePartnerToken") ||
      localStorage.getItem("creditManagerToken") ||
      localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Socket instance
let socket = null;

// Notification API methods
export const notificationAPI = {
  // Socket connection management
  connectSocket: (userId, onNewNotification, onNotificationCount) => {
    if (!userId) return null;

    // Create socket connection if not exists
    if (!socket) {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("staffToken") ||
        localStorage.getItem("customerToken") ||
        localStorage.getItem("bankingPartnerToken") ||
        localStorage.getItem("affiliatePartnerToken") ||
        localStorage.getItem("creditManagerToken") ||
        localStorage.getItem("token");
      socket = io(`${SOCKET_URL}/notifications`, {
        auth: { token },
        query: { token },
        transports: ["websocket", "polling"],
      });

      // Setup event listeners
      socket.on("connect", () => {
        console.log("Connected to notification socket");
        socket.emit("joinNotificationRoom", userId);
      });

      socket.on("disconnect", () => {
        console.log("Disconnected from notification socket");
      });

      socket.on("error", (error) => {
        console.error("Socket error:", error);
      });
    }

    // Setup notification handlers
    if (onNewNotification) {
      socket.on("newNotification", onNewNotification);
    }

    if (onNotificationCount) {
      socket.on("notificationCount", onNotificationCount);
    }

    return socket;
  },

  disconnectSocket: () => {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
  },

  // API methods
  getNotifications: async (userId, page = 1, limit = 10) => {
    try {
      const response = await api.get(`/user/${userId}`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getUnreadCount: async (userId) => {
    try {
      const response = await api.get(`/count/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  markAsRead: async (notificationId) => {
    try {
      const response = await api.put(`/read/${notificationId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await api.put("/read-all");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  sendNotification: async (userId, note, notificationType, senderUserId) => {
    try {
      console.log("🔍 [notificationAPI] sendNotification called with:", {
        userId,
        note,
        notificationType,
        senderUserId,
      });

      const response = await api.post("/send", {
        userId,
        note,
        notificationType,
        senderUserId,
      });

      console.log(
        "✅ [notificationAPI] sendNotification response:",
        response.data
      );
      return response.data;
    } catch (error) {
      console.error("❌ [notificationAPI] sendNotification error:", error);
      throw error.response?.data || error.message;
    }
  },

  sendBulkNotifications: async (
    userIds,
    note,
    notificationType,
    senderUserId
  ) => {
    try {
      const response = await api.post("/send-bulk", {
        userIds,
        note,
        notificationType,
        senderUserId,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};
